const VANCOUVER_TZ = 'America/Vancouver';

// Ensures a timestamp string is parsed as UTC (appends 'Z' if no offset present)
const parseUTC = (timestamp) => {
  if (!timestamp) return null;
  const s = String(timestamp).trim();
  const ts = (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) ? s : s + 'Z';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Normalizes platform names from various formats (URLs, domains, etc.)
 * @param {string} platform - The platform name to normalize
 * @returns {string} - The normalized platform name
 */
export const normalizePlatform = (platform) => {
  if (!platform || typeof platform !== 'string') return '';
  
  // Convert to lowercase for consistent matching
  const lowerPlatform = platform.toLowerCase();
  
  // Handle various platform URL formats
  if (lowerPlatform.includes('discord')) return 'discord';
  if (lowerPlatform.includes('snapchat')) return 'snapchat';
  if (lowerPlatform.includes('instagram') || lowerPlatform.includes('ig')) return 'instagram';
  if (lowerPlatform.includes('tiktok') || lowerPlatform.includes('tik-tok')) return 'tiktok';
  if (lowerPlatform.includes('reddit')) return 'reddit';
  
  // Remove common domains and TLDs for better matching
  return lowerPlatform
    .replace(/\.com$|\.org$|\.net$|\.io$|\.app$|\.co$/, '')  // Remove TLDs
    .replace(/^www\./, '')  // Remove www.
    .replace(/^https?:\/\//, '')  // Remove http:// or https://
    .replace(/\/.*$/, '');  // Remove everything after the first slash
};

/**
 * Parses a server UTC timestamp into a Date object.
 * @param {string} timestamp - The UTC timestamp from the server
 * @returns {Date|null} - The parsed Date or null if invalid
 */
export const adjustServerTime = (timestamp) => {
  if (!timestamp) return null;
  try {
    const d = parseUTC(timestamp);
    if (!d) console.error("Invalid date format:", timestamp);
    return d;
  } catch (e) {
    console.error("Error parsing timestamp:", e, "Timestamp:", timestamp);
    return null;
  }
};

/**
 * Formats a UTC timestamp into a readable string like "Today, 2:30 PM" in Vancouver time (PDT/PST).
 * @param {string} timestamp - The UTC timestamp from the server
 * @returns {string} - The formatted time string
 */
export const formatTime = (timestamp) => {
  if (!timestamp) return 'Unknown time';
  try {
    const date = parseUTC(timestamp);
    if (!date) return 'Invalid date';

    const now = new Date();
    // Compare calendar dates in Vancouver timezone using ISO date strings (YYYY-MM-DD)
    const toVanDate = (d) => d.toLocaleDateString('en-CA', { timeZone: VANCOUVER_TZ });
    const nowStr = toVanDate(now);
    const dateStr = toVanDate(date);
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = toVanDate(yesterday);

    const timeStr = date.toLocaleTimeString('en-US', { timeZone: VANCOUVER_TZ, hour: '2-digit', minute: '2-digit' });
    const displayDate = date.toLocaleDateString('en-US', { timeZone: VANCOUVER_TZ, month: 'short', day: 'numeric' });

    if (dateStr === nowStr) return `Today, ${timeStr}`;
    if (dateStr === yesterdayStr) return `Yesterday, ${timeStr}`;
    return `${displayDate}, ${timeStr}`;
  } catch (e) {
    console.error("Error formatting time:", e, "Timestamp:", timestamp);
    return 'Invalid date';
  }
};

/**
 * Formats a UTC timestamp as a full datetime string in Vancouver time (PDT/PST).
 * @param {string} timestamp - The UTC timestamp from the server
 * @returns {string} - e.g. "05/15/2024, 10:30:45 AM PDT"
 */
export const formatRawTimestamp = (timestamp) => {
  if (!timestamp) return 'UNKNOWN';
  try {
    const date = parseUTC(timestamp);
    if (!date) return 'INVALID';
    return date.toLocaleString('en-US', {
      timeZone: VANCOUVER_TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return 'INVALID';
  }
};

/**
 * Gets the alert type based on content_type
 * @param {string} contentType - The content type from the API
 * @returns {string} - A human-readable alert type
 */
export const getAlertType = (contentType) => {
  if (!contentType) return 'Alert';
  
  if (contentType === 'image') {
    return "Image classified as ";
  } else if (contentType === 'text') {
    return "Message classified as ";
  } else if (contentType === "meme") {
    return "Meme classified as ";
  } else if (contentType === "voice") {
    return "Voice classified as ";
  } else {
    return "Alert";
  }
};

/**
 * Gets the classification label based on the backend classification field.
 * @param {string} classification - The classification from the API ('harmful', 'neutral', or unknown)
 * @returns {string} - Either 'harmful' or 'not-harmful'
 */
export const getSeverity = (classification) => {
  if (!classification) return 'not-harmful';
  return classification === 'harmful' ? 'harmful' : 'not-harmful';
};